import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from "@angular/core";
import {
  BokkzIbDataService,
  IncomeTaxEntity,
  IngehoudenDividendbelasting
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormArray, FormControl, FormGroup} from "@angular/forms";
import {debounceTime, filter, map, Observable, pairwise, shareReplay, startWith, Subscription, take} from "rxjs";
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

type DivFormGroup = FormGroup<{
  rekeningNummer: FormControl<string | null>;
  dividendBox2: FormControl<number | null>;
  dividendbelastingBox2: FormControl<number | null>;
  dividendBox3: FormControl<number | null>;
  dividendbelastingBox3: FormControl<number | null>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-dividendbelasting',
  templateUrl: './dividendbelasting.component.html',
  styleUrls: ['./dividendbelasting.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    SharedUiDetailsModule,
    CommonModule
  ]
})
export class DividendbelastingComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  private changeSubscriptions = new Subscription();

  public formGroup = new FormGroup({
    belasting: new FormArray<DivFormGroup>([]),
  });

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.incomeTaxEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(entity => {
      this.formGroup.reset({}, { emitEvent: false });
      this.updateForm(entity?.aangifte?.voorheffingen?.ingehoudenDividendbelasting || []);
    });
    this.subscribeToChanges();
  }

  ngOnDestroy(): void {
    this.changeSubscriptions.unsubscribe();
  }

  private subscribeToChanges(): void {
    this.changeSubscriptions.unsubscribe();
    this.changeSubscriptions = new Subscription();

    this.subscribeToBedragChanges(this.ingehoudenDividendBelasting);
  }

  private subscribeToBedragChanges(formArray: FormArray<DivFormGroup>): void {
    const extractBedragen = () => formArray.getRawValue().map(item => ({
      dividendbelastingBox2: item.dividendbelastingBox2,
      dividendbelastingBox3: item.dividendbelastingBox3,
    }));

    this.changeSubscriptions.add(
      formArray.valueChanges.pipe(
        debounceTime(500),
        map(() => this.saveForm()),
        map(() => extractBedragen()),
        startWith(extractBedragen()),
        pairwise(),
        filter(([prev, curr]) => JSON.stringify(prev) !== JSON.stringify(curr)),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        this.dataService.calculateLatestIncomeTaxReturn().subscribe();
      })
    );
  }

  get ingehoudenDividendBelasting(): FormArray<DivFormGroup> {
    return this.formGroup.controls.belasting;
  }

  addRij(): void {
    this.ingehoudenDividendBelasting.push(this.createFormGroup(), {emitEvent:false})
  }

  verwijderRij(index: number): void {
    this.ingehoudenDividendBelasting.removeAt(index, {emitEvent: false});
    this.formGroup.updateValueAndValidity({emitEvent: false})
    this.formGroup.markAsDirty({emitEvent: false})
    this.saveForm();
    this.subscribeToChanges();
  }

  createFormGroup(item?: IngehoudenDividendbelasting): FormGroup {
    return new FormGroup({
      dividendBox3: new FormControl<number | null>(item?.dividendBox3 ?? null),
      dividendbelastingBox3: new FormControl<number | null>(item?.dividendbelastingBox3 ?? null),
      dividendBox2: new FormControl<number | null>(item?.dividendBox2 ?? null),
      dividendbelastingBox2: new FormControl<number | null>(item?.dividendbelastingBox2 ?? null),
      rekeningNummer: new FormControl<string | null>(item?.rekeningNummer ?? null),
    })
  }

  private updateForm(data : IngehoudenDividendbelasting[]): void {
    const lijst = data || [];
    const formArray = this.ingehoudenDividendBelasting;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i], {emitEvent: false});
      } else {
        const formGroup = this.createFormGroup(lijst[i]);
        formArray.push(formGroup, {emitEvent: false});
      }
    }
  }

  saveForm(): void {
    this.formGroup.updateValueAndValidity({ emitEvent: false });

    const items: IngehoudenDividendbelasting[] = this.formGroup.getRawValue().belasting
      .filter(item => item.dividendbelastingBox2 !== 0 || item.dividendbelastingBox3 !== 0)
      .map(item => ({
        rekeningNummer: item.rekeningNummer ?? undefined,
        dividendBox2: item.dividendBox2 ?? undefined,
        dividendBox3: item.dividendBox3 ?? undefined,
        dividendbelastingBox2: item.dividendbelastingBox2 ?? undefined,
        dividendbelastingBox3: item.dividendbelastingBox3 ?? undefined,
      }));

    this.dataService.patchIncomeTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          voorheffingen: {
            ...currentEntity.aangifte.voorheffingen,
            ingehoudenDividendbelasting: items
          }
        },
      } as IncomeTaxEntity;
    });
  }
}
